import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Dialog, DialogTitle, DialogContent,
  DialogActions
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { BACKEND_BASE_URL, BASE_URL, CustKycDetails, FAILED, SUCCESS } from "../../constants/Constants";
import { _IsImageFile } from "../../constants/commonUtils";

const DocumentManager = ({ token, shopHash, CustMob }) => {
  const [documents, setDocuments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [preview, setPreview] = useState(null);

  // Fetch existing documents from server
  useEffect(() => {
    if (!CustMob) return;
    getCustomerKyc();
  }, [CustMob]);


  const getCustomerKyc = async () => {
    axios
      .get(`${BACKEND_BASE_URL}get-customer-kyc?token=${token}&shopHash=${shopHash}&mob=${CustMob}`)
      .then((res) => {
        if (res.data.status === SUCCESS) {
          setDocuments(res.data.payLoad.data);
        } else {
          setDocuments([]);
        }
      })
      .catch((err) => console.error("getCustomerKyc", err));
  };

  const setCustomerKyc = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mob", CustMob);
      formData.append("shopHash", shopHash);

      const response = await axios.post(`${BACKEND_BASE_URL}set-kyc-upd`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (err) {
      console.error("setCustomerKyc", err);
    }
    return { status: FAILED };
  }

  const deleteFile = async (id) => {
    axios
      .get(`${BACKEND_BASE_URL}delete-file?token=${token}&shopHash=${shopHash}&id=${id}`)
      .then((res) => {
        if (res.data.status === SUCCESS) {
          setDocuments(res.payLoad.data);
        }
      })
      .catch((err) => console.error("getCustomerKyc", err));
  }

  const handleUpload = async () => {
    for (const fileObj of newFiles) {
      try {
        setNewFiles((prev) =>
          prev.map((f) => f.id === fileObj.id ? { ...f, uploading: true } : f)
        );
        const res = await setCustomerKyc(fileObj.file);
        if (res.status === SUCCESS) {
          const payLoad = res.payLoad;
          const savedDoc = {
            id: payLoad.id,
            url: payLoad.path,
            type: CustKycDetails.INTERNAL,
            fileType: payLoad.fileType,
          };
          setDocuments((prev) => [savedDoc, ...prev]);
          setNewFiles((prev) => prev.filter((f) => f.id !== fileObj.id));
        }

      } catch (err) {
        console.error("handleUpload", err);
      }
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const mapped = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file,
      fileType: file.type,
      type: CustKycDetails.INTERNAL,
      notSaved: true,
    }));
    setNewFiles((prev) => [...prev, ...mapped]);
  };

  // Delete old or new file
  const handleDelete = async (id, isNew) => {
    if (isNew) {
      setNewFiles((prev) => prev.filter((f) => f.id !== id));
    } else {
      await deleteFile(id);
    }
  };

  const handlePreviewOpen = (doc, isNew = false) => {
    const url = isNew ? URL.createObjectURL(doc.file) : `${BASE_URL}/files/${doc.url}`;
    setPreview({ fileType: doc.fileType, url, name: doc.name || url.split("/").pop() });
  };

  const handlePreviewClose = () => {
    setPreview(null);
  };

  // Renderer for file cards
  const renderCard = (item, isNew = false) => (
    <Grid item xs={12} sm={6} md={4} key={item.id}>
      <Card sx={{ position: "relative", cursor: "pointer" }}
        onClick={() => handlePreviewOpen(item, isNew)}>
        {(item?.fileType.startsWith("image") || _IsImageFile(item?.fileType)) ? (
          <CardMedia
            component="img"
            height="110"
            image={isNew ? URL.createObjectURL(item.file) : `${BASE_URL}/files/${item.url}`}
            alt={item.name || "Document"}
          />
        ) : (
          <CardContent>
            <Typography variant="body2">
              {item.name || item.url.split("/").pop()}
            </Typography>
          </CardContent>
        )}
        {/* Delete button */}
        {!item.uploading && (
          <IconButton
            size="small"
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={(e) => {
              e.stopPropagation(); // prevent triggering card click
              handleDelete(item.id, isNew);
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        )}

        {/* Not saved label */}
        {isNew && !item.uploading && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ p: 1, display: "block" }}
          >
            Not Saved
          </Typography>
        )}

        {/* Uploading overlay */}
        {item.uploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Browse Button */}
      <Button
        variant="contained"
        component="label"
        startIcon={<CloudUploadIcon />}
        sx={{ mb: 2, mr: 2 }}
      >
        Browse Files
        <input hidden multiple type="file"
          accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.pdf,.xls,.xlsx,.doc,.docx"
          onChange={handleFileSelect} />
      </Button>
      {newFiles.length > 0 && (
        <Button
          variant="contained"
          color="success"
          onClick={handleUpload}
          sx={{ mb: 2 }}
        >
          Upload Files
        </Button>
      )}

      {/* Files Grid */}
      <Grid container spacing={2}>
        {newFiles.map((file) => renderCard(file, true))}
        {documents.map((doc) => renderCard(doc, false))}

      </Grid>
      <Dialog
        open={!!preview}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{preview?.name}</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          {(preview?.fileType.startsWith("image") || _IsImageFile(preview?.fileType)) ? (
            <img
              src={preview.url}
              alt={preview.name}
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          ) : preview?.fileType === "pdf" || preview?.url?.endsWith(".pdf") ? (
            <iframe
              src={preview.url}
              title={preview.name}
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          ) : (
            <Typography>
              Preview not available.{" "}
              <a href={preview?.url} target="_blank" rel="noopener noreferrer">
                Download
              </a>
            </Typography>
          )}
        </DialogContent>
        {/* Close button at bottom */}
        <DialogActions>
          <Button onClick={handlePreviewClose} variant="contained" color="bRed">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentManager;
